'use client';

import { useEffect, useMemo, useState } from 'react';

import type { OrgPeopleDirectory } from '../components/work-items/people-groups';
import { OrgPeopleResponseSchema } from '../contracts/account';

// ---------------------------------------------------------------------------
// Shared people-discovery hook, used by the work-items People picker, the Reviews
// person filter / org subtabs, and the workspace header's org-scope switcher so
// they discover org members the same way. Extracted from WorkItems.tsx.
// ---------------------------------------------------------------------------

/** An empty people directory — the initial + best-effort-failure state. */
export const EMPTY_DIRECTORY: OrgPeopleDirectory = {
  orgs: [],
  members: [],
  teams: [],
};

interface DirectoryLoad {
  readonly directory: OrgPeopleDirectory;
  readonly failed: boolean;
}

async function fetchDirectory(org: string | null): Promise<DirectoryLoad> {
  const url =
    org === null
      ? '/api/github/org-people'
      : `/api/github/org-people?org=${encodeURIComponent(org)}`;
  try {
    const response = await fetch(url, { cache: 'no-store' });
    if (!response.ok) return { directory: EMPTY_DIRECTORY, failed: true };
    const parsed = OrgPeopleResponseSchema.parse(await response.json());
    return {
      directory: {
        orgs: parsed.orgs,
        members: parsed.members,
        teams: parsed.teams,
      },
      failed: false,
    };
  } catch {
    // Best-effort discovery: leave the directory empty on any failure.
    return { directory: EMPTY_DIRECTORY, failed: true };
  }
}

// Coalesce concurrent fetches for the same scope into one request: in the personal
// workspace the header's switcher and the data view both discover the directory on
// mount, so this dedupes that duplicate GET. The entry is dropped once the request
// settles, so a later mount (e.g. a tab switch) still refreshes.
const inFlight = new Map<string, Promise<DirectoryLoad>>();

function loadDirectory(org: string | null): Promise<DirectoryLoad> {
  const key = org ?? '';
  const existing = inFlight.get(key);
  if (existing !== undefined) return existing;
  const promise = fetchDirectory(org).finally(() => {
    inFlight.delete(key);
  });
  inFlight.set(key, promise);
  return promise;
}

/** Clear the in-flight request cache. Exposed for deterministic tests. */
export function clearOrgPeopleCache(): void {
  inFlight.clear();
}

/** A discovered directory plus whether its fetch has settled (success or failure). */
export type LoadedOrgPeople = OrgPeopleDirectory & {
  readonly failed: boolean;
  readonly loaded: boolean;
};

/**
 * Discover pickable people for a workspace. In an org workspace this is that
 * org's members + teams; in the personal workspace (`org === null`) it's everyone
 * across the orgs the viewer belongs to, each annotated with their org(s).
 * Best-effort — a failure leaves an empty directory. `loaded` distinguishes "still
 * fetching" from "settled, but the viewer has zero orgs" (an empty directory looks
 * the same either way) so callers can act on a genuinely-empty scope.
 */
export function useOrgPeople(org: string | null): LoadedOrgPeople {
  const [directory, setDirectory] =
    useState<OrgPeopleDirectory>(EMPTY_DIRECTORY);
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let active = true;
    // Clear immediately so a workspace switch never shows the previous org's
    // people while the new fetch is in flight (or if it fails).
    setDirectory(EMPTY_DIRECTORY);
    setLoaded(false);
    setFailed(false);
    // Share the fetch (don't abort it on unmount — another consumer may still want
    // it); just ignore the result once this consumer is gone.
    void loadDirectory(org).then((result) => {
      if (active) {
        setDirectory(result.directory);
        setFailed(result.failed);
        setLoaded(true);
      }
    });
    return (): void => {
      active = false;
    };
  }, [org]);
  return useMemo(
    () => ({ ...directory, failed, loaded }),
    [directory, failed, loaded],
  );
}
